import { Box, Button, Stack, Typography } from '@mui/material';
import { scheduleClock } from './RouteScheduleSummary';
import React, { useMemo, useState } from 'react';

const MAP_WIDTH = 1100;
const MAP_HEIGHT = 500;
const TILE_SIZE = 256;
const MIN_ZOOM = 8;
const MAX_ZOOM = 17;

function coordinate(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function buildRouteStops(items = []) {
    const stops = new Map();
    items
        .filter((item) => item.selected)
        .forEach((item) => {
            const lat = coordinate(item.location?.lat);
            const lng = coordinate(item.location?.lng);
            if (lat === null || lng === null) return;
            const key = String(item.location_id || (lat.toFixed(6) + ',' + lng.toFixed(6)));
            const order = Number(item.route_order);
            const current = stops.get(key);
            if (!current) {
                stops.set(key, {
                    key,
                    location_id: item.location_id || null,
                    location_name: item.location_name || 'Ubicación sin nombre',
                    lat,
                    lng,
                    order: Number.isFinite(order) && order > 0 ? order : 999,
                    machines: [item.machine_name || String(item.machine_id)]
                });
                return;
            }
            current.machines.push(item.machine_name || String(item.machine_id));
            if (Number.isFinite(order) && order > 0) current.order = Math.min(current.order, order);
        });

    return Array.from(stops.values())
        .sort((left, right) => left.order - right.order || left.location_name.localeCompare(right.location_name))
        .map((stop, index) => ({ ...stop, display_order: index + 1 }));
}

export function reorderStops(stops, sourceKey, targetKey) {
    const sourceIndex = stops.findIndex((stop) => stop.key === sourceKey);
    const targetIndex = stops.findIndex((stop) => stop.key === targetKey);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return stops;
    const reordered = stops.slice();
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    return reordered.map((stop, index) => ({ ...stop, display_order: index + 1 }));
}

function worldPoint(lat, lng, zoom) {
    const scale = TILE_SIZE * Math.pow(2, zoom);
    const boundedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
    const sin = Math.sin((boundedLat * Math.PI) / 180);
    return {
        x: ((lng + 180) / 360) * scale,
        y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale
    };
}

export function createMapLayout(stops, width = MAP_WIDTH, height = MAP_HEIGHT) {
    if (!stops.length) return null;
    const padding = 58;
    let zoom = MAX_ZOOM;
    let projected = [];

    for (; zoom >= MIN_ZOOM; zoom -= 1) {
        projected = stops.map((stop) => ({ stop, ...worldPoint(stop.lat, stop.lng, zoom) }));
        const xs = projected.map((point) => point.x);
        const ys = projected.map((point) => point.y);
        if (
            Math.max(...xs) - Math.min(...xs) <= width - padding * 2 &&
            Math.max(...ys) - Math.min(...ys) <= height - padding * 2
        ) break;
    }

    const xs = projected.map((point) => point.x);
    const ys = projected.map((point) => point.y);
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
    const originX = centerX - width / 2;
    const originY = centerY - height / 2;
    const firstTileX = Math.floor(originX / TILE_SIZE);
    const lastTileX = Math.floor((originX + width) / TILE_SIZE);
    const firstTileY = Math.floor(originY / TILE_SIZE);
    const lastTileY = Math.floor((originY + height) / TILE_SIZE);
    const tileLimit = Math.pow(2, zoom);
    const tiles = [];

    for (let tileX = firstTileX; tileX <= lastTileX; tileX += 1) {
        for (let tileY = firstTileY; tileY <= lastTileY; tileY += 1) {
            if (tileY < 0 || tileY >= tileLimit) continue;
            const wrappedX = ((tileX % tileLimit) + tileLimit) % tileLimit;
            tiles.push({
                key: tileX + ':' + tileY,
                href: 'https://tile.openstreetmap.org/' + zoom + '/' + wrappedX + '/' + tileY + '.png',
                x: tileX * TILE_SIZE - originX,
                y: tileY * TILE_SIZE - originY
            });
        }
    }

    return {
        zoom,
        tiles,
        points: projected.map(({ stop, x, y }) => ({
            ...stop,
            x: x - originX,
            y: y - originY
        }))
    };
}

export default function RouteCityMap({ items, editable = false, onOrderChange, schedule, stale }) {
    const stops = useMemo(() => buildRouteStops(items), [items]);
    const layout = useMemo(() => createMapLayout(stops), [stops]);
    const [draggedStop, setDraggedStop] = useState(null);
    const [zoom, setZoom] = useState(1);

    const moveStop = (sourceKey, targetKey) => {
        const reordered = reorderStops(stops, sourceKey, targetKey);
        if (reordered === stops || typeof onOrderChange !== 'function') return;
        onOrderChange(reordered.map((stop) => stop.key));
    };

    if (!layout) {
        return <div className="text-500 p-3">No hay coordenadas disponibles para mostrar el recorrido.</div>;
    }

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(260px, 2fr)' }, gap: 2 }}>
            <Box sx={{ position: { xs: 'static', lg: 'sticky' }, top: 100, alignSelf: 'start', '& svg': { minHeight: { xs: 260, sm: 480 } } }}><div className="border-1 surface-border border-round overflow-hidden" style={{ background: '#eef2f6', position: 'relative' }}>
                <Stack sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, bgcolor: 'white', borderRadius: 2 }}><Button aria-label="Acercar mapa" disabled={zoom >= 3} onClick={() => setZoom(value => Math.min(3, value + 0.5))}>+</Button><Button aria-label="Alejar mapa" disabled={zoom <= 1} onClick={() => setZoom(value => Math.max(1, value - 0.5))}>−</Button><Button aria-label="Restablecer mapa" onClick={() => setZoom(1)}>↺</Button></Stack>
                <svg
                    viewBox={`${MAP_WIDTH * (1 - 1 / zoom) / 2} ${MAP_HEIGHT * (1 - 1 / zoom) / 2} ${MAP_WIDTH / zoom} ${MAP_HEIGHT / zoom}`}
                    role="img"
                    aria-label="Mapa de la ciudad con el orden de paradas"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    preserveAspectRatio="xMidYMid meet"
                >
                    {layout.tiles.map((tile) => (
                        <image
                            key={tile.key}
                            href={tile.href}
                            onError={event => { event.currentTarget.style.visibility = 'hidden'; }}
                            x={tile.x}
                            y={tile.y}
                            width={TILE_SIZE}
                            height={TILE_SIZE}
                        />
                    ))}
                    <polyline
                        fill="none"
                        stroke="#ffffff"
                        strokeOpacity="0.9"
                        strokeWidth="10"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={layout.points.map((point) => point.x + ',' + point.y).join(' ')}
                    />
                    <polyline
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={layout.points.map((point) => point.x + ',' + point.y).join(' ')}
                    />
                    {layout.points.map((point) => (
                        <g key={point.key} aria-label={'Parada ' + point.display_order + ': ' + point.location_name}>
                            <circle cx={point.x} cy={point.y} r="18" fill="#ffffff" stroke="#2563eb" strokeWidth="4" />
                            <text
                                x={point.x}
                                y={point.y + 5}
                                textAnchor="middle"
                                fill="#1d4ed8"
                                fontSize="14"
                                fontWeight="700"
                            >
                                {point.display_order}
                            </text>
                        </g>
                    ))}
                </svg>
            </div><Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>{stops.length} paradas</Typography>
            <div className="flex flex-wrap justify-content-between gap-2 mt-2 text-500">
                <small>
                    El mapa muestra la ciudad y conecta las paradas en el orden calculado. La línea es una referencia visual; distancia y tiempo provienen de Google Routes.
                </small>
                <small>
                    © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>
                </small>
            </div>
            </Box><ol className="mt-0 mb-0" style={{ listStyle: 'none', padding: 0, maxHeight: 620, overflowY: 'auto' }}>
                {stops.map((stop, index) => (
                    <li
                        key={stop.key}
                        draggable={editable}
                        onDragStart={() => setDraggedStop(stop.key)}
                        onDragEnd={() => setDraggedStop(null)}
                        onDragOver={(event) => {
                            if (editable) event.preventDefault();
                        }}
                        onDrop={(event) => {
                            event.preventDefault();
                            if (draggedStop) moveStop(draggedStop, stop.key);
                            setDraggedStop(null);
                        }}
                        className={'mb-2 p-3 border-1 surface-border border-round ' + (draggedStop === stop.key ? 'surface-200' : 'surface-0')}
                        style={{ cursor: editable ? 'grab' : 'default', borderRadius: 14, padding: 18, background: 'white', marginBottom: 12 }}
                    >
                        <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                            <div>
                                {editable && <i className="pi pi-bars mr-2 text-500" aria-hidden="true" />}
                                <span style={{ display: 'inline-grid', placeItems: 'center', width: 30, height: 30, borderRadius: '50%', color: 'white', background: '#205fa9', marginRight: 12 }}>{stop.display_order}</span><b>{stop.location_name}</b>
                                <span className="text-600" style={{ display: 'block', marginTop: 8, marginLeft: 42 }}>{stop.machines.slice(0, 3).join(', ')}{stop.machines.length > 3 && <details><summary>+ {stop.machines.length - 3} máquinas</summary>{stop.machines.slice(3).join(', ')}</details>}</span>
                                <small className="block mt-1">{stale ? 'Estimación pendiente de recalcular' : 'Llegada: ' + scheduleClock(schedule?.stops?.find(entry => String(entry.location_id) === String(stop.location_id))?.arrival_seconds)} · {schedule?.stops?.find(entry => String(entry.location_id) === String(stop.location_id))?.outside_window ? 'Fuera de horario' : schedule?.stops?.find(entry => String(entry.location_id) === String(stop.location_id))?.window ? 'Horario configurado' : 'Horario sin confirmar'}</small>
                            </div>
                            {editable && (
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        className="p-button p-component p-button-text p-button-sm"
                                        aria-label={'Mover ' + stop.location_name + ' hacia arriba'}
                                        disabled={index === 0}
                                        onClick={() => moveStop(stop.key, stops[index - 1]?.key)}
                                    >
                                        <span className="pi pi-arrow-up" />
                                    </button>
                                    <button
                                        type="button"
                                        className="p-button p-component p-button-text p-button-sm"
                                        aria-label={'Mover ' + stop.location_name + ' hacia abajo'}
                                        disabled={index === stops.length - 1}
                                        onClick={() => moveStop(stop.key, stops[index + 1]?.key)}
                                    >
                                        <span className="pi pi-arrow-down" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </Box>
    );
}
